/**
 * Slice Redux pour les étudiants
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { etudiantService } from '../../services/api.service';
import { Etudiant, PaginatedResponse } from '../../types';

interface EtudiantState {
  etudiants: Etudiant[];
  currentEtudiant: Etudiant | null;
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: EtudiantState = {
  etudiants: [],
  currentEtudiant: null,
  totalCount: 0,
  loading: false,
  error: null,
};

// Thunks asynchrones
export const fetchEtudiants = createAsyncThunk(
  'etudiants/fetchAll',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response = await etudiantService.getAll(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Erreur lors de la récupération des étudiants');
    }
  }
);

export const fetchEtudiantById = createAsyncThunk(
  'etudiants/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const etudiant = await etudiantService.getById(id);
      return etudiant;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Étudiant introuvable');
    }
  }
);

export const fetchEtudiantByMatricule = createAsyncThunk(
  'etudiants/fetchByMatricule',
  async (matricule: string, { rejectWithValue }) => {
    try {
      const etudiant = await etudiantService.getByMatricule(matricule);
      return etudiant;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Étudiant introuvable');
    }
  }
);

export const createEtudiant = createAsyncThunk(
  'etudiants/create',
  async (data: Partial<Etudiant> | FormData, { rejectWithValue }) => {
    try {
      const etudiant = await etudiantService.create(data);
      return etudiant;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la création');
    }
  }
);

export const updateEtudiant = createAsyncThunk(
  'etudiants/update',
  async ({ id, data }: { id: number; data: Partial<Etudiant> | FormData }, { rejectWithValue }) => {
    try {
      const etudiant = await etudiantService.update(id, data);
      return etudiant;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la mise à jour');
    }
  }
);

export const deleteEtudiant = createAsyncThunk(
  'etudiants/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await etudiantService.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  }
);

const etudiantSlice = createSlice({
  name: 'etudiants',
  initialState,
  reducers: {
    clearCurrentEtudiant: (state) => {
      state.currentEtudiant = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all
    builder
      .addCase(fetchEtudiants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEtudiants.fulfilled, (state, action) => {
        state.loading = false;
        // Gérer les deux formats : paginé {results, count} ou tableau direct
        if (Array.isArray(action.payload)) {
          state.etudiants = action.payload;
          state.totalCount = action.payload.length;
        } else if (action.payload?.results) {
          state.etudiants = action.payload.results;
          state.totalCount = action.payload.count;
        } else {
          state.etudiants = [];
          state.totalCount = 0;
        }
      })
      .addCase(fetchEtudiants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch by ID
    builder
      .addCase(fetchEtudiantById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEtudiantById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEtudiant = action.payload;
      })
      .addCase(fetchEtudiantById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch by matricule
    builder
      .addCase(fetchEtudiantByMatricule.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEtudiantByMatricule.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEtudiant = action.payload;
      })
      .addCase(fetchEtudiantByMatricule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create
    builder
      .addCase(createEtudiant.pending, (state) => {
        state.loading = true;
      })
      .addCase(createEtudiant.fulfilled, (state, action) => {
        state.loading = false;
        state.etudiants.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createEtudiant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update
    builder
      .addCase(updateEtudiant.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateEtudiant.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.etudiants.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.etudiants[index] = action.payload;
        }
        if (state.currentEtudiant?.id === action.payload.id) {
          state.currentEtudiant = action.payload;
        }
      })
      .addCase(updateEtudiant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete
    builder
      .addCase(deleteEtudiant.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteEtudiant.fulfilled, (state, action) => {
        state.loading = false;
        state.etudiants = state.etudiants.filter(e => e.id !== action.payload);
        state.totalCount -= 1;
        if (state.currentEtudiant?.id === action.payload) {
          state.currentEtudiant = null;
        }
      })
      .addCase(deleteEtudiant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCurrentEtudiant, clearError } = etudiantSlice.actions;
export default etudiantSlice.reducer;
